import { SerializedError, createAsyncThunk } from '@reduxjs/toolkit';
import { makeRequest, ApiRequestError } from './apiRequest';
import { HttpErrorStatus } from './httpStatusCodes';
import { setErrorToastOnce } from './errorToast.util';
import { ApiRequestParams, ApiRequestDefaults } from './apiRequestParams';
import { EndpointDefinition } from './endpointDefinition';

export const createApiThunk = <
  RESPONSE extends object | void,
  BODY extends object,
  PARAMS extends ApiRequestParams
>(
  endpoint: EndpointDefinition<RESPONSE, BODY, PARAMS>,
  caughtErrors?: HttpErrorStatus[]
) => {
  const thunk = createAsyncThunk<
    RESPONSE,
    {
      params?: PARAMS;
      body?: BODY;
      defaults?: ApiRequestDefaults;
    },
    {
      rejectValue: CustomSerializedError;
    }
  >(
    endpoint.name,
    async (
      request: { params?: PARAMS; body?: BODY; defaults?: ApiRequestDefaults },
      thunkAPI
    ) => {
      try {
        const result = await makeRequest(endpoint, request);
        return result;
      } catch (err) {
        const apiError = err as ApiRequestError;
        setErrorToastOnce(apiError, thunkAPI.dispatch);

        const serializedError = err as SerializedError;
        if (
          caughtErrors &&
          caughtErrors.find(
            httpError => httpError.toString() === serializedError.code
          )
        ) {
          return thunkAPI.rejectWithValue({
            ...serializedError,
            isAccessDenied: apiError.isAccessDenied,
            errorIsCaught: true,
          });
        }
        return thunkAPI.rejectWithValue({
          ...serializedError,
          isAccessDenied: apiError.isAccessDenied,
          errorIsCaught: false,
        });
      }
    }
  );

  return thunk;
};

export type CustomSerializedError = SerializedError & {
  errorIsCaught: boolean;
  isAccessDenied?: boolean;
};
